import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SidebarNav from './SidebarNav';
import SidebarHelp from './SidebarHelp';
import { toggleTheme } from '../../store/slices/themeSlice';
import { RootState } from '../../store/store';
import sunIcon from '../../assets/icons/Icon-Header-Sun.svg';
import moonIcon from '../../assets/icons/Icon-Header-moon.svg';

interface MobileMenuOverlayProps {
  isOpen: boolean;
  isSubuser: boolean;
  onClose: () => void;
}

const MobileMenuOverlay: React.FC<MobileMenuOverlayProps> = ({
  isOpen,
  isSubuser,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedTheme = useSelector((state: RootState) => state.theme.selectedTheme);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="mobile-menu-overlay" role="dialog" aria-modal="true" aria-label={t('navigation')}>
      <div className="mobile-menu-body">
        <nav className="sidebar-nav mobile-menu-nav" aria-label={t('navigation')}>
          <SidebarNav isSubuser={isSubuser} onNavigate={onClose} />
          <ul className="mobile-menu-theme-block">
            <li className="nav-item mobile-menu-theme-item">
              <button
                type="button"
                className="mobile-menu-theme"
                role="switch"
                aria-checked={selectedTheme === 'dark'}
                aria-label={t('theme')}
                onClick={() => dispatch(toggleTheme(selectedTheme === 'light' ? 'dark' : 'light'))}
              >
                <span className="nav-item-icon">
                  <img
                    src={selectedTheme === 'dark' ? sunIcon : moonIcon}
                    alt=""
                    aria-hidden="true"
                  />
                </span>
                <span className="menu-item-label">
                  {selectedTheme === 'dark'
                    ? t('mobile_menu_theme_light')
                    : t('mobile_menu_theme_dark')}
                </span>
                <span className="mobile-menu-theme__switch" aria-hidden="true">
                  <span
                    className={`mobile-menu-theme__toggle toggle-switch${
                      selectedTheme === 'dark' ? ' toggle-switch--on' : ''
                    }`}
                  >
                    <span className="toggle-switch__thumb" />
                  </span>
                </span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
      <SidebarHelp />
    </div>
  );
};

export default MobileMenuOverlay;
